// apps/web/src/lib/health.ts
// Apple Health integration, full scope (plan §1.3):
// - READS steps, active energy, and workouts (7-day window) into
//   /api/health/sync — workouts become read-only activity rows, active energy
//   is display/coaching only (the budget rule never reads it).
// - WRITES logged body weight back to Health via the local CalolyHealth Swift
//   plugin (build >= 15). Builds without it silently skip the mirror.
// HealthKit without the entitlement crashes, so everything is gated on the
// native build number / plugin availability.
import { api } from "./api";
import { isNativeApp } from "./native";
import type { ActivityType, HealthSyncDay, HealthWorkoutInput } from "@shared/types";

const FLAG = "caloly_health_connected";
const SCOPE_KEY = "caloly_health_scopes";
const CURRENT_SCOPE = 2;          // v1 = steps only; v2 = + active energy, workouts, weight write
const MIN_NATIVE_BUILD = 11;
const SYNC_DAYS = 7;

type CapWindow = Window & { Capacitor?: { isPluginAvailable?: (name: string) => boolean; Plugins?: Record<string, unknown> } };

async function calolyHealth(): Promise<{
  requestAuthorization: () => Promise<{ granted: boolean }>;
  saveWeight: (o: { weightKg: number; date?: string }) => Promise<{ saved: boolean }>;
} | null> {
  const cap = (window as CapWindow).Capacitor;
  if (!cap?.isPluginAvailable?.("CalolyHealth")) return null;
  return (cap.Plugins as Record<string, never>)["CalolyHealth"] ?? null;
}

export function healthConnected(): boolean {
  try { return localStorage.getItem(FLAG) === "1"; } catch { return false; }
}

function scopeVersion(): number {
  try { return Number(localStorage.getItem(SCOPE_KEY) ?? "0") || 0; } catch { return 0; }
}

function setScopeVersion(v: number): void {
  try { localStorage.setItem(SCOPE_KEY, String(v)); } catch { /* ignore */ }
}

/** Whether the Apple Health row should exist at all on this device/build. */
export async function healthSupported(): Promise<boolean> {
  if (!isNativeApp()) return false;
  try {
    const { App } = await import("@capacitor/app");
    const info = await App.getInfo();
    if (Number(info.build) < MIN_NATIVE_BUILD) return false;
    const { Health } = await import("capacitor-health");
    const { available } = await Health.isHealthAvailable();
    return available;
  } catch {
    return false;
  }
}

/** Request all read scopes (+ weight share where the plugin exists), remember
 *  the choice, and run a first sync. HealthKit never reveals whether read
 *  access was granted — denied permissions just yield empty results. */
export async function connectHealth(): Promise<boolean> {
  const local = await calolyHealth();
  if (local) {
    // One combined sheet: steps/active energy/workouts read + bodyMass share.
    await local.requestAuthorization();
  } else {
    const { Health } = await import("capacitor-health");
    await Health.requestHealthPermissions({
      permissions: ["READ_STEPS", "READ_ACTIVE_CALORIES", "READ_WORKOUTS"],
    });
  }
  try { localStorage.setItem(FLAG, "1"); } catch { /* ignore */ }
  setScopeVersion(CURRENT_SCOPE);
  await syncHealth().catch(() => { /* first sync is best-effort */ });
  return true;
}

/** Already-connected users from the steps-only era get the new scopes once. */
async function maybeUpgradeScopes(): Promise<void> {
  if (!healthConnected() || scopeVersion() >= CURRENT_SCOPE) return;
  try {
    await connectHealth(); // iOS only prompts for not-yet-decided types
  } catch { /* keep the old scopes */ }
}

const pad = (n: number) => String(n).padStart(2, "0");
const localDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// HK workout type → app activity type. Raw HK type is preserved in the note.
const WORKOUT_TYPE_MAP: Record<string, ActivityType> = {
  walking: "walk", running: "run", trackAndField: "run",
  cycling: "cycle", handCycling: "cycle",
  hiking: "hike",
  swimming: "swim", waterFitness: "swim",
  traditionalStrengthTraining: "strength", functionalStrengthTraining: "strength",
  coreTraining: "strength", crossTraining: "strength",
};

/** Pull the last week of steps, active energy, and workouts; push to the API. */
export async function syncHealth(): Promise<void> {
  const { Health } = await import("capacitor-health");
  const start = new Date();
  start.setDate(start.getDate() - (SYNC_DAYS - 1));
  start.setHours(0, 0, 0, 0);
  const startISO = start.toISOString();
  const endISO = new Date().toISOString();

  const days = new Map<string, HealthSyncDay>();
  const dayOf = (date: string): HealthSyncDay => {
    let d = days.get(date);
    if (!d) { d = { date }; days.set(date, d); }
    return d;
  };

  const [stepsAgg, energyAgg] = await Promise.all([
    Health.queryAggregated({ startDate: startISO, endDate: endISO, dataType: "steps", bucket: "day" })
      .catch(() => ({ aggregatedData: [] })),
    Health.queryAggregated({ startDate: startISO, endDate: endISO, dataType: "active-calories", bucket: "day" })
      .catch(() => ({ aggregatedData: [] })),
  ]);
  for (const s of stepsAgg.aggregatedData ?? []) {
    const v = Math.max(0, Math.round(s.value ?? 0));
    if (v > 0) dayOf(localDate(new Date(s.startDate))).steps = v;
  }
  for (const s of energyAgg.aggregatedData ?? []) {
    const v = Math.max(0, Math.round(s.value ?? 0));
    if (v > 0) dayOf(localDate(new Date(s.startDate))).active_kcal = v;
  }

  // Workouts: every day in the window gets a workouts array (even empty) so
  // the server can mirror deletions.
  try {
    const { workouts } = await Health.queryWorkouts({
      startDate: startISO, endDate: endISO,
      includeHeartRate: false, includeRoute: false, includeSteps: false,
    });
    for (let i = 0; i < SYNC_DAYS; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      const day = dayOf(localDate(d));
      if (!day.workouts) day.workouts = [];
    }
    for (const w of workouts ?? []) {
      const startDate = new Date(w.startDate);
      const durationMin = Math.round((w.duration ?? 0) / 60);
      if (durationMin < 5) continue; // skip micro-workouts
      const mapped: HealthWorkoutInput = {
        hk_id: String(w.id).slice(0, 64),
        type: WORKOUT_TYPE_MAP[w.workoutType ?? ""] ?? "other",
        start_at: Math.floor(startDate.getTime() / 1000),
        duration_min: Math.min(1440, Math.max(1, durationMin)),
        distance_km: w.distance != null && w.distance > 0 ? Math.round((w.distance / 1000) * 100) / 100 : null,
        kcal_burned: Math.min(5000, Math.max(0, Math.round(w.calories ?? 0))),
        note: [w.sourceName, w.workoutType].filter(Boolean).join(" · ").slice(0, 120) || null,
      };
      const day = dayOf(localDate(startDate));
      (day.workouts ??= []).push(mapped);
    }
    // Cap per server limits.
    for (const d of days.values()) if (d.workouts && d.workouts.length > 20) d.workouts = d.workouts.slice(0, 20);
  } catch { /* workouts scope denied — steps/energy still sync */ }

  const payload = [...days.values()];
  if (payload.length === 0) return;
  await api.healthSync(payload);
  window.dispatchEvent(new CustomEvent("caloly:health-synced"));
}

/** Back-compat alias used by the settings row. */
export const syncHealthSteps = syncHealth;

/** Mirror a logged weight into Apple Health (fire-and-forget; no read scope
 *  for body mass exists anywhere, so an echo loop is structurally impossible). */
async function mirrorWeight(weightKg: number, date?: string): Promise<void> {
  if (!healthConnected()) return;
  const local = await calolyHealth();
  if (!local) return; // pre-15 build — no write support
  await local.saveWeight({ weightKg, date }).catch(() => { /* denied/unavailable */ });
}

/** Background sync on app open + resume. Call once from the app shell. */
export function startHealthAutoSync(): () => void {
  if (!isNativeApp()) return () => {};

  const onWeight = (e: Event) => {
    const detail = (e as CustomEvent<{ weight_kg?: number; measured_on?: string }>).detail;
    if (detail?.weight_kg) void mirrorWeight(detail.weight_kg, detail.measured_on);
  };
  window.addEventListener("caloly:weight-logged", onWeight);

  if (healthConnected()) {
    void maybeUpgradeScopes().then(() => syncHealth()).catch(() => { /* offline/denied */ });
  }
  let remove: (() => void) | null = null;
  void import("@capacitor/app").then(({ App }) =>
    App.addListener("resume", () => {
      if (healthConnected()) void syncHealth().catch(() => { /* ignore */ });
    }).then((h) => { remove = () => h.remove(); }),
  );
  return () => {
    window.removeEventListener("caloly:weight-logged", onWeight);
    remove?.();
  };
}

/** Disconnect: stop syncing and mirroring. Already-imported history stays —
 *  past recalibrations consumed it; deleting would falsify TDEE history. */
export function disconnectHealth(): void {
  try {
    localStorage.removeItem(FLAG);
    localStorage.removeItem(SCOPE_KEY);
  } catch { /* ignore */ }
}
